/**
 * Location-pin client (SMS location-pin feature, Task 7) — the two public,
 * unauthenticated endpoints a customer's pin link talks to.
 *
 *   GET  /v1/online-ordering/location-pin/{token}           -> {valid, expiresAt}
 *   GET  /v1/online-ordering/location-pin/{token}/coverage  -> {served, branchName, …}
 *   POST /v1/online-ordering/location-pin/{token}           -> {ok}
 *
 * These go through the online-ordering BFF rather than straight to
 * customer_service's `/v1/messaging/location-pin/...` routes. That path is
 * already public on BOTH gateways, so it needs no gateway config change —
 * and a config replaces the whole spec, which is how the staging gateway was
 * wiped once. It is also the only way this works on production, where the
 * direct messaging routes have never been declared and fall through to the
 * authenticated `/v1/messaging/**` catch-all as a 401.
 *
 * `skipAuth: true` — no customer sign-in: whoever opens the SMS link is not
 * expected to have an account. Being an `/v1/online-ordering` path, apiFetch
 * waits for the tenant `X-API-KEY`, so the page must call
 * `ensureStorefrontApiKey()` first (see PinLocationScreen).
 *
 * An unknown, expired, or already-used token all resolve `{valid: false}`
 * with HTTP 200 by design — there is no way to tell the three apart, and
 * PinLocationScreen must not try to.
 */
import { apiFetch } from "./http";

export interface LocationPinStatus {
  valid: boolean;
  expiresAt: string | null;
}

export interface LocationPinSubmitInput {
  lat: number;
  lng: number;
  accuracyMeters: number | null;
  /** 'gps' = a device fix the customer did not move; 'manual' = a pin they
   * dragged the map to or picked out of search. The server drops any
   * accuracy figure sent with a manual pin — a hand-placed pin is an
   * assertion, not a measurement, and the agent and driver must be able to
   * tell the two apart. */
  pinSource: "gps" | "manual";
  address?: string | null;
  placeName?: string | null;
}

/** Delivery coverage for the coordinate currently under the pin. Read-only
 * and free: it never burns the single-use token, so the customer can move
 * the pin as often as they like before submitting once.
 *
 * `served: false` means "we can't promise delivery here" — it covers a real
 * out-of-zone answer AND a zone lookup that failed or a token that has
 * died. It must therefore drive an advisory, never a block on submitting:
 * refusing a correct pin because our zone service had a bad minute throws
 * away the one thing the agent asked the customer for. */
export interface LocationPinCoverage {
  served: boolean;
  branchName?: string | null;
  areaName?: string | null;
  feeAmount?: number | null;
  feeCurrency?: string | null;
  minDeliveryDurationInMinutes?: number | null;
  maxDeliveryDurationInMinutes?: number | null;
}

export interface LocationPinSubmitResult {
  ok: boolean;
}

export const locationPinApi = {
  check(token: string): Promise<LocationPinStatus> {
    return apiFetch<LocationPinStatus>(
      `/v1/online-ordering/location-pin/${encodeURIComponent(token)}`,
      { method: "GET", skipAuth: true },
    );
  },

  coverage(token: string, lat: number, lng: number): Promise<LocationPinCoverage> {
    const qs = new URLSearchParams({ lat: String(lat), lng: String(lng) });
    return apiFetch<LocationPinCoverage>(
      `/v1/online-ordering/location-pin/${encodeURIComponent(token)}/coverage?${qs}`,
      { method: "GET", skipAuth: true },
    );
  },

  submit(token: string, input: LocationPinSubmitInput): Promise<LocationPinSubmitResult> {
    return apiFetch<LocationPinSubmitResult>(
      `/v1/online-ordering/location-pin/${encodeURIComponent(token)}`,
      { method: "POST", body: input, skipAuth: true },
    );
  },
};
